/**
 * Dialog per duplicare un preventivo verso un ALTRO intestatario (cliente
 * per vendita, fornitore per acquisto), cosi' Walter non deve ricopiare a
 * mano oggetto/condizioni/righe per riusarli con un cliente diverso
 * (Task 7, backend gia' pronto in ordini/backend/services.py::duplica_preventivo).
 *
 * Il picker e' un Combobox con ricerca SERVER-SIDE su /api/v1/soggetti
 * (parametro `q`, limite basso, debounce), stesso pattern gia' adottato
 * anche dal picker cliente in NuovoPreventivoVendita.tsx (bonificato dal
 * precedente fetch `?limit=500` filtrato in memoria, Task 8c): la ricerca
 * resta esaustiva qualunque sia la dimensione dell'anagrafica.
 *
 * L'endpoint POST /{id}/duplica accetta solo un intestatario dello stesso
 * tipo del preventivo (cliente per vendita, fornitore per acquisto) e
 * rifiuta il resto con 422: la UI propone quindi solo soggetti di quel
 * tipo. Copia oggetto/condizioni/righe ma NON destinatari extra,
 * rappresentante e PDF caricato dell'originale (appartengono
 * all'intestatario di origine): l'avviso qui sotto lo rende esplicito
 * perche' non e' un comportamento ovvio.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type ComboboxOption,
} from '@floraltech/ui';

interface SoggettoOpt {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preventivoId: string;
  tipo: 'vendita' | 'acquisto';
  token: string | null;
}

export function DuplicaPreventivoDialog({ open, onOpenChange, preventivoId, tipo, token }: Props) {
  const navigate = useNavigate();
  const tipoSoggetto = tipo === 'vendita' ? 'cliente' : 'fornitore';

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SoggettoOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selezionato, setSelezionato] = useState<SoggettoOpt | null>(null);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Reset dello stato interno solo all'apertura: evita di trascinare la
  // ricerca/selezione di una duplica precedente.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelezionato(null);
      setSaving(false);
      setErrore(null);
    }
  }, [open]);

  // Identifica la ricerca corrente: incrementata prima di ogni fetch, per
  // scartare risposte tardive di query precedenti (sorpasso) senza toccare
  // risultati/caricamento di quella in corso. Stesso pattern gia' adottato
  // in CaricaSistemaDialog.tsx (modulo email).
  const ricercaGenerazioneRef = useRef(0);

  // Ricerca soggetti debounced e server-side (mai in-memory, vedi commento
  // in testa al file).
  useEffect(() => {
    if (!open || selezionato) return;
    const timer = setTimeout(async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      ricercaGenerazioneRef.current += 1;
      const generazione = ricercaGenerazioneRef.current;
      setLoading(true);
      setErrore(null);
      try {
        const params = new URLSearchParams({ tipo: tipoSoggetto, q: query.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Una ricerca piu' recente e' gia' partita: questa risposta e'
        // superata, va ignorata senza toccare risultati ne' errore.
        if (ricercaGenerazioneRef.current !== generazione) return;
        if (res.ok) {
          const data = await res.json();
          if (ricercaGenerazioneRef.current !== generazione) return;
          setResults(Array.isArray(data) ? data : (data.items ?? []));
        } else {
          setResults([]);
          setErrore(`Ricerca soggetti fallita (${res.status})`);
        }
      } catch {
        // Errore di rete: se nel frattempo e' partita una ricerca piu'
        // recente il sorpasso e' gia' gestito sopra, quindi qui e' un
        // errore reale della ricerca corrente.
        if (ricercaGenerazioneRef.current === generazione) {
          setResults([]);
          setErrore('Errore di rete durante la ricerca: verifica la connessione e riprova.');
        }
      } finally {
        if (ricercaGenerazioneRef.current === generazione) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, selezionato, tipoSoggetto, token]);

  const label = (s: SoggettoOpt) =>
    s.ragione_sociale || [s.nome, s.cognome].filter(Boolean).join(' ') || '-';

  const handleConferma = async () => {
    if (!selezionato || saving) return;
    setSaving(true);
    setErrore(null);
    try {
      const res = await fetch(`/api/v1/preventivi/${preventivoId}/duplica`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ intestatario_id: selezionato.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        // detail e' stringa per gli errori di dominio (422 da ValueError);
        // la validazione automatica di FastAPI (es. UUID malformato)
        // restituisce invece un ARRAY di oggetti, mai da renderizzare cosi'.
        setErrore(typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`);
        return;
      }
      const nuovo = await res.json();
      onOpenChange(false);
      // Stesso instradamento per tipo dell'handler di riformula: la duplica
      // eredita il tipo del preventivo di origine.
      navigate(tipo === 'vendita' ? `/vendite/preventivi/${nuovo.id}` : `/preventivi/${nuovo.id}`);
    } catch {
      setErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setSaving(false);
    }
  };

  const titolo = tipo === 'vendita' ? 'Duplica per altro cliente' : 'Duplica per altro fornitore';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Crea una nuova bozza con oggetto, condizioni e righe copiati da questo preventivo.
            Destinatari email aggiuntivi, rappresentante e PDF caricato dell&apos;originale{' '}
            <strong>non</strong> vengono copiati: appartengono all&apos;intestatario di origine.
          </p>
          <div>
            <label className="text-xs text-muted-foreground">
              {tipo === 'vendita' ? 'Nuovo cliente' : 'Nuovo fornitore'}
            </label>
            {!selezionato ? (
              <>
                <Combobox
                  value={query}
                  onInputChange={setQuery}
                  onSelect={(opt) => {
                    const s = results.find((x) => x.id === opt.value);
                    if (s) setSelezionato(s);
                  }}
                  options={results.map<ComboboxOption>((s) => ({ value: s.id, label: label(s) }))}
                  loading={loading}
                  placeholder={tipo === 'vendita' ? "Digita nome del cliente..." : 'Digita nome del fornitore...'}
                  autoFocus
                />
                {/* Il Combobox condiviso (ui_core) mostra la tendina "Nessun
                    risultato" solo se options.length > 0, loading, oppure e'
                    passato onCreateNew (qui non passato apposta: qui non si
                    crea un soggetto al volo). Con zero risultati la tendina
                    sparisce del tutto e "Ricerca..." lascia il posto al
                    nulla, indistinguibile da una ricerca rotta (review
                    finale, punto 2). Messaggio esplicito qui sotto, senza
                    toccare il componente condiviso. */}
                {!loading && !errore && query.trim() && results.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nessun risultato per &quot;{query.trim()}&quot;.
                  </p>
                )}
              </>
            ) : (
              <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                <span>{label(selezionato)}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelezionato(null)}>
                  Cambia
                </Button>
              </div>
            )}
          </div>
          {errore && <p className="text-sm text-destructive">{errore}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annulla
          </Button>
          <Button type="button" onClick={() => void handleConferma()} disabled={saving || !selezionato}>
            {saving ? 'Duplicazione...' : 'Duplica'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
